import { Injectable }    from '@angular/core';
import {
	TaxationReportingFormTypeEnum,
	TaxationReportingStatusEnum,
	TaxationReportingStatusUpdate
}                        from '@app/modules/taxation/taxation-reporting/models/taxation-reporting.model';
import {
	FormTypeEnumDTO,
	RegistrationProtocolStatusEnumDTO,
	RegistrationProtocolStatusUpdateEnumDTO
}                        from '@openApiGenerated/eric-wrapper-api';
import { LoggerService } from '@shared/services/logger.service';
import { ObjectUtils }   from '@utils/helpers';

@Injectable( {
	providedIn: 'root'
} )
export class TaxationMapperService {

	constructor(
		private readonly _logger: LoggerService
	) { }

	get taxation() {
		return {
			isNotYetTaxReportedToHasLastSuccessRPPreviewDate: this.isNotYetTaxReportedToHasLastSuccessRPPreviewDate.bind( this ) as typeof this.isNotYetTaxReportedToHasLastSuccessRPPreviewDate,

			taxationReportingFormTypeDtoToTmo: this.taxationReportingFormTypeDtoToTmo.bind( this ) as typeof this.taxationReportingFormTypeDtoToTmo,
			taxationReportingFormTypeTmoToDto: this.taxationReportingFormTypeTmoToDto.bind( this ) as typeof this.taxationReportingFormTypeTmoToDto,
			taxationReportingStatusDtoToTmo: this.taxationReportingStatusDtoToTmo.bind( this ) as typeof this.taxationReportingStatusDtoToTmo,
			taxationReportingStatusTmoToDto: this.taxationReportingStatusTmoToDto.bind( this ) as typeof this.taxationReportingStatusTmoToDto,
			taxationReportingStatusListDtoToTmo: this.taxationReportingStatusListDtoToTmo.bind( this ) as typeof this.taxationReportingStatusListDtoToTmo,
			taxationReportingStatusListTmoToDto: this.taxationReportingStatusListTmoToDto.bind( this ) as typeof this.taxationReportingStatusListTmoToDto,
			taxationReportingStatusUpdateTmoToDto: this.taxationReportingStatusUpdateDtoToTmo.bind( this ) as typeof this.taxationReportingStatusUpdateDtoToTmo
		};
	}

	// BP list filter param, placed here because closely related to the taxation topic and because there is no dedicated BP mapper service (yet)
	private isNotYetTaxReportedToHasLastSuccessRPPreviewDate( isNotYetTaxReported: boolean ): boolean {
		if ( ObjectUtils.isNullish( isNotYetTaxReported ) ) { return isNotYetTaxReported; }
		return !isNotYetTaxReported;
	}


	// Form type
	private taxationReportingFormTypeDtoToTmo( formType: FormTypeEnumDTO ): TaxationReportingFormTypeEnum {
		const formTypeDtoToTmoMap: { [key in FormTypeEnumDTO]: TaxationReportingFormTypeEnum } = {
			[ FormTypeEnumDTO.AO146A ]: TaxationReportingFormTypeEnum.AO146A
		};

		const tmoFormType: TaxationReportingFormTypeEnum = formTypeDtoToTmoMap[ formType ] ?? null;
		if ( !tmoFormType ) {
			this._logger.warn( `taxationReportingFormTypeMap has no matching TMO value ${ formType }` );
		}
		return tmoFormType;
	}

	private taxationReportingFormTypeTmoToDto( formType: TaxationReportingFormTypeEnum ): FormTypeEnumDTO {
		const formTypeTmoToDtoMap: { [key in TaxationReportingFormTypeEnum]: FormTypeEnumDTO } = {
			[ TaxationReportingFormTypeEnum.AO146A ]: FormTypeEnumDTO.AO146A
		};

		const dtoFormType: FormTypeEnumDTO = formTypeTmoToDtoMap[ formType ] ?? null;
		if ( !dtoFormType ) {
			this._logger.warn( `taxationReportingFormTypeMap has no matching DTO value ${ formType }` );
		}
		return dtoFormType;
	}


	// Status
	private taxationReportingStatusDtoToTmo( status: RegistrationProtocolStatusEnumDTO ): TaxationReportingStatusEnum {
		const statusDtoToTmoMap: { [key in RegistrationProtocolStatusEnumDTO]: TaxationReportingStatusEnum } = {
			[ RegistrationProtocolStatusEnumDTO.ACKNOWLEDGED ]: TaxationReportingStatusEnum.ACKNOWLEDGED,
			[ RegistrationProtocolStatusEnumDTO.CREATED ]: TaxationReportingStatusEnum.CREATING,
			[ RegistrationProtocolStatusEnumDTO.CONFIRMED ]: TaxationReportingStatusEnum.SUBMITTING,
			[ RegistrationProtocolStatusEnumDTO.EXPIRED ]: TaxationReportingStatusEnum.EXPIRED,
			[ RegistrationProtocolStatusEnumDTO.OUTDATED ]: TaxationReportingStatusEnum.OUTDATED,
			[ RegistrationProtocolStatusEnumDTO.PREVIEW_FAILED ]: TaxationReportingStatusEnum.PREVIEW_FAILED,
			[ RegistrationProtocolStatusEnumDTO.READY ]: TaxationReportingStatusEnum.READY,
			[ RegistrationProtocolStatusEnumDTO.SUBMIT_FAILED ]: TaxationReportingStatusEnum.SUBMIT_FAILED,
			[ RegistrationProtocolStatusEnumDTO.SUCCESS ]: TaxationReportingStatusEnum.SUCCESS
		};

		const tmoStatusValue: TaxationReportingStatusEnum = statusDtoToTmoMap[ status ] ?? null;
		if ( !tmoStatusValue ) {
			this._logger.warn( `taxationReportingStatusMap has no matching TMO value ${ status }` );
		}
		return tmoStatusValue;
	}

	private taxationReportingStatusTmoToDto( status: TaxationReportingStatusEnum ): RegistrationProtocolStatusEnumDTO {
		const statusTmoToDtoMap: { [key in TaxationReportingStatusEnum]: RegistrationProtocolStatusEnumDTO } = {
			[ TaxationReportingStatusEnum.ACKNOWLEDGED ]: RegistrationProtocolStatusEnumDTO.ACKNOWLEDGED,
			[ TaxationReportingStatusEnum.CREATING ]: RegistrationProtocolStatusEnumDTO.CREATED,
			[ TaxationReportingStatusEnum.EXPIRED ]: RegistrationProtocolStatusEnumDTO.EXPIRED,
			[ TaxationReportingStatusEnum.OUTDATED ]: RegistrationProtocolStatusEnumDTO.OUTDATED,
			[ TaxationReportingStatusEnum.PREVIEW_FAILED ]: RegistrationProtocolStatusEnumDTO.PREVIEW_FAILED,
			[ TaxationReportingStatusEnum.READY ]: RegistrationProtocolStatusEnumDTO.READY,
			[ TaxationReportingStatusEnum.SUBMIT_FAILED ]: RegistrationProtocolStatusEnumDTO.SUBMIT_FAILED,
			[ TaxationReportingStatusEnum.SUBMITTING ]: RegistrationProtocolStatusEnumDTO.CONFIRMED,
			[ TaxationReportingStatusEnum.SUCCESS ]: RegistrationProtocolStatusEnumDTO.SUCCESS
		};

		const dtoStatusValue: RegistrationProtocolStatusEnumDTO = statusTmoToDtoMap[ status ] ?? null;
		if ( !dtoStatusValue ) {
			this._logger.warn( `taxationReportingStatusMap has no matching DTO value ${ status }` );
		}
		return dtoStatusValue;
	}

	private taxationReportingStatusListDtoToTmo( statusList: RegistrationProtocolStatusEnumDTO[] ): TaxationReportingStatusEnum[] {
		const dtoStatusList: TaxationReportingStatusEnum[] = statusList?.map( this.taxationReportingStatusDtoToTmo );
		return dtoStatusList;
	}

	private taxationReportingStatusListTmoToDto( statusList: TaxationReportingStatusEnum[] ): RegistrationProtocolStatusEnumDTO[] {
		const dtoStatusList: RegistrationProtocolStatusEnumDTO[] = statusList?.map( this.taxationReportingStatusTmoToDto );
		return dtoStatusList;
	}


	// Status update
	private taxationReportingStatusUpdateDtoToTmo( newStatus: TaxationReportingStatusUpdate ): RegistrationProtocolStatusUpdateEnumDTO {
		return this.taxationReportingStatusTmoToDto( newStatus ) as unknown as RegistrationProtocolStatusUpdateEnumDTO;
	}
}
